import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { adminStatsAPI } from '../services/adminApi';
import { setAdminNavHandoff, consumeAdminNavHandoff } from '../utils/adminNavFilters';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const BRAND = { blue: '#043663', red: '#D52B1E', muted: '#64748b' };

const Page = styled.div`
  max-width: 1280px;
`;

const Header = styled.div`
  margin-bottom: 1.25rem;
  h2 {
    margin: 0 0 0.35rem;
    font-size: 1.65rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  p {
    margin: 0;
    color: ${BRAND.muted};
    font-size: 0.9rem;
    max-width: 48rem;
    line-height: 1.45;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const Field = styled.div`
  label {
    display: block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    margin-bottom: 0.2rem;
  }
  select,
  input {
    min-width: 160px;
    padding: 0.45rem 0.65rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 0.875rem;
    background: #fff;
  }
`;

const Note = styled.div`
  margin-bottom: 1.25rem;
  padding: 0.75rem 1rem;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  font-size: 0.8125rem;
  color: #9a3412;
  line-height: 1.45;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Kpi = styled.div`
  background: ${(p) => p.$bg || `linear-gradient(135deg, ${BRAND.blue} 0%, #032a4a 100%)`};
  color: #fff;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.08);
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  ${(p) =>
    p.$clickable
      ? `
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 10px rgba(15, 23, 42, 0.14);
    }
  `
      : ''}
  .n {
    font-size: 1.45rem;
    font-weight: 800;
    line-height: 1.15;
  }
  .l {
    margin-top: 0.25rem;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.9;
  }
  .hint {
    margin-top: 0.2rem;
    font-size: 0.7rem;
    opacity: 0.8;
  }
`;

const ClickRow = styled.tr`
  cursor: pointer;
  &:hover td {
    background: #f8fafc;
    color: ${BRAND.blue};
  }
`;

const Section = styled.section`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.15rem 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  h3 {
    margin: 0 0 0.35rem;
    font-size: 1.05rem;
    color: #0f172a;
  }
  .sub {
    margin: 0 0 0.85rem;
    font-size: 0.8rem;
    color: ${BRAND.muted};
    line-height: 1.4;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 2px solid #e2e8f0;
    color: #475569;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  td {
    padding: 0.5rem 0.4rem;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
  }
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 1.2fr) 2fr auto;
  gap: 0.65rem;
  align-items: center;
  margin-bottom: 0.45rem;
  font-size: 0.8rem;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  border-radius: 6px;
  padding: 0.15rem 0.2rem;
  ${(p) =>
    p.$clickable
      ? `
    &:hover {
      background: #f1f5f9;
    }
  `
      : ''}
  .label {
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .val {
    font-weight: 700;
    color: #0f172a;
    min-width: 3rem;
    text-align: right;
  }
`;

const BarTrack = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${(p) => Math.min(100, p.$pct || 0)}%;
  background: linear-gradient(90deg, ${BRAND.blue}, #0a558c);
  border-radius: 999px;
`;

const Empty = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${BRAND.muted};
  font-size: 0.875rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin: 0 -0.25rem;
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${(p) => (p.$paid ? '#d1fae5' : '#fef3c7')};
  color: ${(p) => (p.$paid ? '#065f46' : '#92400e')};
`;

const WarnRow = styled.tr`
  background: ${(p) => (p.$warn ? '#fff7ed' : 'transparent')};
`;

function fmtNum(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

function fmtChf(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} CHF`;
}

function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n)}%`;
}

function maxOf(rows, key) {
  return Math.max(1, ...rows.map((r) => Number(r[key]) || 0), 1);
}

function fmtRegDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

const PersonCell = styled.div`
  font-size: 0.8rem;
  line-height: 1.35;
  max-width: 220px;
  .name {
    font-weight: 600;
    color: #0f172a;
  }
  .email {
    color: #64748b;
    font-size: 0.72rem;
    word-break: break-all;
  }
  .meta {
    margin-top: 0.2rem;
    color: #64748b;
    font-size: 0.7rem;
  }
  .dup {
    display: inline-block;
    margin-top: 0.25rem;
    padding: 0.08rem 0.4rem;
    border-radius: 999px;
    background: #fee2e2;
    color: #991b1b;
    font-size: 0.65rem;
    font-weight: 700;
  }
`;

function CompactPerson({ person, showInviteMode }) {
  if (!person) return '—';
  return (
    <PersonCell>
      <div className="name">{person.name || '—'}</div>
      {person.email ? <div className="email">{person.email}</div> : null}
      <div style={{ marginTop: '0.25rem' }}>
        <StatusPill $paid={person.status === 'paid'}>{person.status}</StatusPill>
        {person.total_chf != null ? (
          <span style={{ marginLeft: '0.35rem', fontSize: '0.72rem', color: '#475569' }}>
            {fmtChf(person.total_chf)}
          </span>
        ) : null}
      </div>
      {showInviteMode ? (
        <div className="meta">
          {person.invoice_mode === 'existing_team_partner'
            ? 'On roster · pay to stay'
            : 'Invite · joins after pay'}
          {person.invitation_status ? ` · ${person.invitation_status}` : ''}
        </div>
      ) : null}
      {person.duplicate_count > 1 ? (
        <span className="dup" title="Same person had multiple invites/invoices; showing the best one">
          {person.duplicate_count}× invites merged
        </span>
      ) : null}
    </PersonCell>
  );
}

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: #64748b;
  button {
    border: 1px solid #cbd5e1;
    background: #fff;
    border-radius: 8px;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
    cursor: pointer;
    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }
`;

const PACKAGE_PAGE_SIZE = 25;

function BarList({ rows, labelKey, valueKey, onRowClick }) {
  if (!rows?.length) return <Empty>No data for this filter.</Empty>;
  const max = maxOf(rows, valueKey);
  return rows.map((r) => {
    const v = Number(r[valueKey]) || 0;
    const clickable = typeof onRowClick === 'function';
    return (
      <BarRow
        key={`${r[labelKey]}-${v}`}
        $clickable={clickable}
        onClick={clickable ? () => onRowClick(r) : undefined}
        role={clickable ? 'button' : undefined}
        title={clickable ? `Open with filter: ${r[labelKey]}` : undefined}
      >
        <div className="label" title={r[labelKey]}>
          {r[labelKey]}
        </div>
        <BarTrack>
          <BarFill $pct={(v / max) * 100} />
        </BarTrack>
        <div className="val">{fmtNum(v)}</div>
      </BarRow>
    );
  });
}

export default function AdminLeagueStatistics({ onNavigate }) {
  const { adminUser } = useAdminAuth();
  const canSeeFinancials =
    adminUser?.isSuperAdmin === true || adminUser?.role === 'super_admin';
  const [preferredPhaseId, setPreferredPhaseId] = useState('');
  const [canton, setCanton] = useState('');
  const [packageSearch, setPackageSearch] = useState('');
  const [unpaidPartnersOnly, setUnpaidPartnersOnly] = useState(false);
  const [packagePage, setPackagePage] = useState(0);

  useEffect(() => {
    const h = consumeAdminNavHandoff();
    if (!h) return;
    if (h.section && h.section !== 'league-statistics') return;
    if (h.seasonId != null && h.seasonId !== '') setPreferredPhaseId(String(h.seasonId));
    if (h.canton != null && h.canton !== '') setCanton(String(h.canton));
  }, []);

  const {
    campaignId,
    seasonId,
    onCampaignChange,
    onPhaseChange,
    seasonOptions,
    phaseOptions,
    allowAll,
  } = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
    preferredPhaseId,
  });

  const go = (section, filters = {}) => {
    const handoff = {
      section,
      seasonId: seasonId || filters.seasonId || undefined,
      canton: canton.trim() || filters.canton || undefined,
      ...filters,
    };
    setAdminNavHandoff(handoff);
    if (typeof onNavigate === 'function') onNavigate(section, handoff);
  };

  const params = useMemo(
    () => ({
      ...(campaignId ? { campaignId } : {}),
      ...(seasonId ? { seasonId } : {}),
      ...(canton.trim() ? { canton: canton.trim() } : {}),
    }),
    [campaignId, seasonId, canton]
  );

  const { data, isLoading, error } = useQuery(
    ['admin-league-statistics', campaignId, params],
    () => adminStatsAPI.getLeagueAnalytics(params),
    { keepPreviousData: true }
  );

  const payload = data?.data || data || null;
  const k = payload?.kpis || {};
  const d = payload?.disclaimers || {};
  const showFinancials = canSeeFinancials && payload?.financialsVisible !== false;

  const cantonOptions = useMemo(() => {
    const set = new Set();
    (payload?.byCanton || []).forEach((r) => {
      if (r.canton && r.canton !== 'Unknown') set.add(r.canton);
    });
    return Array.from(set).sort();
  }, [payload?.byCanton]);

  const seasonRegs = payload?.registrationsBySeason || [];
  const seasonRev = payload?.revenueBySeason || [];
  const byCanton = payload?.byCanton || [];
  const bySkill = payload?.bySkill || [];
  const revBySkill = payload?.revenueBySkill || [];
  const byClub = payload?.registrationsByClub || [];
  const trend = payload?.registrationTrend || [];
  const matchStatus = payload?.matchesByStatus || [];
  const packageTeams = payload?.registrationPackageTeams || [];
  const packageSummary = payload?.registrationPackagesSummary || [];

  const filteredPackageTeams = useMemo(() => {
    const q = packageSearch.trim().toLowerCase();
    return packageTeams.filter((g) => {
      if (unpaidPartnersOnly && !g.unpaid_partner) return false;
      if (!q) return true;
      const hay = [
        g.team_name,
        g.season_name,
        g.path_label,
        g.partner_wait_label,
        g.background_label,
        g.creator?.name,
        g.creator?.email,
        g.creator?.package,
        ...(g.partners || []).flatMap((p) => [p.name, p.email, p.package, p.invitation_status]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [packageTeams, packageSearch, unpaidPartnersOnly]);

  useEffect(() => {
    setPackagePage(0);
  }, [packageSearch, unpaidPartnersOnly, seasonId, campaignId]);

  const packagePageCount = Math.max(1, Math.ceil(filteredPackageTeams.length / PACKAGE_PAGE_SIZE));
  const safePackagePage = Math.min(packagePage, packagePageCount - 1);
  const pagedPackageTeams = useMemo(() => {
    const start = safePackagePage * PACKAGE_PAGE_SIZE;
    return filteredPackageTeams.slice(start, start + PACKAGE_PAGE_SIZE);
  }, [filteredPackageTeams, safePackagePage]);

  return (
    <Page>
      <Header>
        <h2>League Statistics</h2>
        <p>
          Platform overview for registrations, matches
          {showFinancials ? ', and checkout revenue' : ''} from existing league data.
          Click KPIs or rows to open existing Teams / Matches / Analytics / Attention screens with filters applied.
        </p>
      </Header>

      <FilterBar>
        <AdminSeasonPhaseFilters
          campaignId={campaignId}
          seasonId={seasonId}
          seasonOptions={seasonOptions}
          phaseOptions={phaseOptions}
          onCampaignChange={onCampaignChange}
          onPhaseChange={onPhaseChange}
          allowAll={allowAll}
        />
        <Field>
          <label>Canton</label>
          <input
            list="league-stats-cantons"
            placeholder="All cantons"
            value={canton}
            onChange={(e) => setCanton(e.target.value)}
          />
          <datalist id="league-stats-cantons">
            {cantonOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
      </FilterBar>

      <Note>
        With a phase selected, team counts use Original ⭐ signups only (same as Teams). All phases =
        unique teams (not seats × phases). Season auto-enrol seats appear in the by-phase Paid seats
        column.
        {d.bySeasonCharts ? ` ${d.bySeasonCharts}` : ''} Drill-downs are additive — existing screens are unchanged.
      </Note>

      {isLoading && !payload ? (
        <Empty>Loading league statistics…</Empty>
      ) : error ? (
        <Empty style={{ color: BRAND.red }}>
          {error?.response?.data?.message || error?.message || 'Failed to load statistics'}
        </Empty>
      ) : (
        <>
          <KpiGrid>
            <Kpi
              $clickable
              onClick={() => go('attention', {})}
              title="Open Attention center"
            >
              <div className="n">{fmtNum(k.registrationsPaid)}</div>
              <div className="l">Paid teams</div>
              <div className="hint">
                {fmtNum(k.registrationsTotal)} unique
                {k.registrationSeats != null && k.registrationSeats !== k.registrationsTotal
                  ? ` · ${fmtNum(k.registrationSeats)} phase seats`
                  : ''}
                {k.registrationsPending ? ` · ${fmtNum(k.registrationsPending)} pending` : ''}
              </div>
            </Kpi>
            <Kpi
              $clickable
              onClick={() => go('teams', { isActive: 'true' })}
              title="Open Teams (active)"
            >
              <div className="n">{fmtNum(k.activeTeams)}</div>
              <div className="l">Active teams</div>
            </Kpi>
            <Kpi
              $clickable
              onClick={() => go('players', {})}
              title="Open Players"
            >
              <div className="n">{fmtNum(k.activePlayers)}</div>
              <div className="l">Active players</div>
            </Kpi>
            <Kpi
              $clickable
              $bg={`linear-gradient(135deg, ${BRAND.red} 0%, #b52217 100%)`}
              onClick={() => go('matches', { matchStatus: 'completed' })}
              title="Open Matches (completed)"
            >
              <div className="n">{fmtNum(k.completedMatches)}</div>
              <div className="l">Completed matches</div>
              <div className="hint">{fmtNum(k.remainingMatches)} remaining — click for completed</div>
            </Kpi>
            <Kpi
              $clickable
              $bg="linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
              onClick={() => go('analytics', { analyticsTab: 'ops' })}
              title="Open Analytics Attention"
            >
              <div className="n">{fmtNum(k.forfeitMatchesHeuristic)}</div>
              <div className="l">Forfeit matches</div>
              <div className="hint">Heuristic → Analytics</div>
            </Kpi>
            <Kpi
              $clickable
              $bg="linear-gradient(135deg, #b45309 0%, #92400e 100%)"
              onClick={() => go('teams', { isActive: 'false' })}
              title="Open Teams (inactive)"
            >
              <div className="n">{fmtNum(k.withdrawnOrInactiveTeams)}</div>
              <div className="l">Withdrawals</div>
              <div className="hint">Inactive TSR proxy → Teams</div>
            </Kpi>
            <Kpi
              $clickable
              onClick={() => go('attention', {})}
              title="Open Attention"
            >
              <div className="n">{fmtNum(k.activeSeasons)}</div>
              <div className="l">Active phases</div>
            </Kpi>
            {showFinancials ? (
              <Kpi $bg="linear-gradient(135deg, #047857 0%, #065f46 100%)">
                <div className="n">{fmtChf(k.revenueTotalChf)}</div>
                <div className="l">Revenue total</div>
                <div className="hint">
                  Checkout {fmtChf(k.revenueCheckoutChf)} · Invoices {fmtChf(k.revenueInvoicesChf)}
                </div>
              </Kpi>
            ) : null}
            <Kpi
              $clickable
              onClick={() => go('registration-orders', { registrationOrdersStatus: 'pending' })}
              title="Open Orders (pending)"
            >
              <div className="n">{fmtPct(k.conversionRate)}</div>
              <div className="l">Conversion</div>
              <div className="hint">
                {fmtNum(k.conversionCompleted)} / {fmtNum(k.conversionStarted)} · pending → Orders
              </div>
            </Kpi>
          </KpiGrid>

          <Section>
            <h3>Registrations by phase</h3>
            <p className="sub">
              Bars = original ⭐ signups that started in that phase (same rule as Teams). Carried = not ⭐ here,
              but already seated in an earlier phase of the same season (by start date — first phase is
              always 0 carried). Paid seats may still include backfilled rows.
            </p>
            {!seasonRegs.length ? (
              <Empty>No phases found.</Empty>
            ) : (
              <>
                <BarList
                  rows={[...seasonRegs].reverse()}
                  labelKey="season_name"
                  valueKey="original_paid"
                  onRowClick={(r) => go('teams', { seasonId: r.season_id, isActive: 'all' })}
                />
                <Table style={{ marginTop: '0.75rem' }}>
                  <thead>
                    <tr>
                      <th>Phase</th>
                      <th>Original ⭐</th>
                      <th>Carried</th>
                      <th>Paid seats</th>
                      <th>Pending</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seasonRegs.map((r) => (
                      <ClickRow
                        key={r.season_id}
                        onClick={() => go('teams', { seasonId: r.season_id, isActive: 'all' })}
                        title="Open Teams for original signups in this phase"
                      >
                        <td>
                          {r.season_name}
                          {r.season_index ? (
                            <span style={{ color: '#64748b', fontWeight: 500 }}> · #{r.season_index}</span>
                          ) : null}
                        </td>
                        <td>{fmtNum(r.original_paid ?? r.original)}</td>
                        <td>{fmtNum(r.carried_paid ?? r.carried)}</td>
                        <td>{fmtNum(r.paid)}</td>
                        <td>{fmtNum(r.pending)}</td>
                        <td>{r.season_status || '—'}</td>
                      </ClickRow>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Section>

          <TwoCol>
            <Section>
              <h3>Registrations by canton</h3>
              <p className="sub">
                Distinct teams by canton
                {seasonId ? ' (Original ⭐ for selected phase)' : ' (all phases, unique teams)'}. Click →
                Teams.
              </p>
              <BarList
                rows={byCanton}
                labelKey="canton"
                valueKey="registrations"
                onRowClick={(r) =>
                  go('teams', {
                    canton: r.canton === 'Unknown' ? '' : r.canton,
                    isActive: 'all',
                  })
                }
              />
            </Section>
            <Section>
              <h3>Registrations by category level</h3>
              <p className="sub">
                Distinct teams by skill band
                {seasonId ? ' (Original ⭐)' : ''}.
              </p>
              <BarList
                rows={bySkill}
                labelKey="band"
                valueKey="teams"
                onRowClick={() => go('analytics', { analyticsTab: 'overview' })}
              />
            </Section>
          </TwoCol>

          <Section>
            <h3>Registrations by club</h3>
            <p className="sub">{d.registrationsByClub || 'Free-text home club (Partial). Click → Analytics clubs.'}</p>
            {!byClub.length ? (
              <Empty>No club strings in scope.</Empty>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Home club</th>
                    <th>Regs</th>
                    <th>Paid</th>
                    <th>Teams</th>
                    <th>Players</th>
                  </tr>
                </thead>
                <tbody>
                  {byClub.map((r) => (
                    <ClickRow
                      key={r.club_label}
                      onClick={() => go('analytics', { analyticsTab: 'clubs' })}
                      title="Open Analytics clubs tab"
                    >
                      <td>{r.club_label}</td>
                      <td>{fmtNum(r.registrations)}</td>
                      <td>{fmtNum(r.paid_registrations)}</td>
                      <td>{fmtNum(r.teams)}</td>
                      <td>{fmtNum(r.players)}</td>
                    </ClickRow>
                  ))}
                </tbody>
              </Table>
            )}
          </Section>

          <TwoCol>
            <Section>
              <h3>Matches by status</h3>
              <p className="sub">
                Completed {fmtNum(k.completedMatches)} · Remaining {fmtNum(k.remainingMatches)} · Forfeit
                heuristic {fmtNum(k.forfeitMatchesHeuristic)}. Click a status to open Matches.
              </p>
              <BarList
                rows={matchStatus}
                labelKey="status"
                valueKey="count"
                onRowClick={(r) => go('matches', { matchStatus: r.status })}
              />
              {d.forfeitMatches ? <p className="sub">{d.forfeitMatches}</p> : null}
              {d.withdrawals ? <p className="sub">{d.withdrawals}</p> : null}
            </Section>
            <Section>
              <h3>Conversion</h3>
              <p className="sub">{d.conversion}</p>
              <Table>
                <tbody>
                  <tr>
                    <td>Started (DB rows)</td>
                    <td>{fmtNum(k.conversionStarted)}</td>
                  </tr>
                  <tr>
                    <td>Completed (paid)</td>
                    <td>{fmtNum(k.conversionCompleted)}</td>
                  </tr>
                  <tr>
                    <td>Rate</td>
                    <td>{fmtPct(k.conversionRate)}</td>
                  </tr>
                </tbody>
              </Table>
              <p className="sub" style={{ marginTop: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => go('registration-orders', { registrationOrdersStatus: 'pending' })}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: BRAND.blue,
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '0.8rem',
                  }}
                >
                  Open pending Orders →
                </button>
              </p>
            </Section>
          </TwoCol>

          {showFinancials ? (
            <>
              <Section>
                <h3>Revenue by phase</h3>
                <p className="sub">{d.revenue}</p>
                {!seasonRev.length ? (
                  <Empty>No revenue rows.</Empty>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <th>Phase</th>
                        <th>Checkout</th>
                        <th>Invoices</th>
                        <th>Total</th>
                        <th>Checkout rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seasonRev.map((r) => (
                        <ClickRow
                          key={r.season_id}
                          onClick={() => go('teams', { seasonId: r.season_id })}
                          title="Open Teams for this phase"
                        >
                          <td>{r.season_name}</td>
                          <td>{fmtChf(r.revenue_checkout_chf)}</td>
                          <td>{fmtChf(r.revenue_invoices_chf)}</td>
                          <td>{fmtChf(r.revenue_total_chf)}</td>
                          <td>{fmtNum(r.checkout_rows)}</td>
                        </ClickRow>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Section>

              <Section>
                <h3>Packages and invitations</h3>
                <p className="sub">{d.registrationPackages}</p>
                <p className="sub" style={{ marginTop: '-0.35rem' }}>
                  Package invoices paid {fmtChf(k.revenueInvoicesChf)} · still pending {fmtChf(k.invoicePendingChf)} ·
                  teams with a partner still waiting to pay {fmtNum(k.unpaidPartnerTeams)}. 2 members on a continued
                  team does not mean the partner has paid — check Partner status. Use this table for package math — not
                  the Checkout + Invoices total above.
                </p>
                {packageSummary.length ? (
                  <Table style={{ marginBottom: '1rem', maxWidth: '40rem' }}>
                    <thead>
                      <tr>
                        <th>Package</th>
                        <th>Paid players</th>
                        <th>Pending players</th>
                        <th>Paid</th>
                        <th>Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packageSummary.map((r) => (
                        <tr key={r.package_label}>
                          <td>{r.package_label}</td>
                          <td>{fmtNum(r.paid_players)}</td>
                          <td>{fmtNum(r.pending_players)}</td>
                          <td>{fmtChf(r.paid_chf)}</td>
                          <td>{fmtChf(r.pending_chf)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : null}
                <FilterBar style={{ marginBottom: '0.75rem', boxShadow: 'none' }}>
                  <Field>
                    <label>Search</label>
                    <input
                      value={packageSearch}
                      onChange={(e) => setPackageSearch(e.target.value)}
                      placeholder="Team, player, package…"
                    />
                  </Field>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={unpaidPartnersOnly}
                      onChange={(e) => setUnpaidPartnersOnly(e.target.checked)}
                    />
                    Waiting partner pay
                  </label>
                </FilterBar>
                {!filteredPackageTeams.length ? (
                  <Empty>No package invoices for this filter.</Empty>
                ) : (
                  <>
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Registered</th>
                            <th>Phase</th>
                            <th>Team</th>
                            <th>Path</th>
                            <th>Members</th>
                            <th>Partner status</th>
                            <th>Registered player</th>
                            <th>Package</th>
                            <th>Other player</th>
                            <th>Package</th>
                            <th>Paid</th>
                            <th>Expected</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedPackageTeams.map((g, idx) => {
                            const partners = g.partners || [];
                            const sr = safePackagePage * PACKAGE_PAGE_SIZE + idx + 1;
                            return (
                              <WarnRow
                                key={`${g.team_id || 'x'}-${g.season_id}`}
                                $warn={g.unpaid_partner}
                                title={g.background_label || undefined}
                              >
                                <td style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{sr}</td>
                                <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#475569' }}>
                                  {fmtRegDate(g.registered_at)}
                                </td>
                                <td>{g.season_name}</td>
                                <td style={{ fontWeight: 600 }}>{g.team_name}</td>
                                <td>{g.path_label}</td>
                                <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                  {fmtNum(g.roster_count)} roster · {fmtNum(g.paid_players)} paid
                                </td>
                                <td style={{ fontSize: '0.8rem' }}>
                                  {g.partner_wait_label}
                                  {g.completion_status === 'awaiting_partner' ? (
                                    <div style={{ fontSize: '0.72rem', color: '#92400e' }}>Seat: awaiting partner</div>
                                  ) : null}
                                </td>
                                <td>
                                  <CompactPerson person={g.creator} />
                                </td>
                                <td style={{ fontSize: '0.8rem' }}>{g.creator?.package || '—'}</td>
                                <td>
                                  {partners.length ? (
                                    partners.map((p) => (
                                      <CompactPerson
                                        key={p.invoice_id || `${p.email}-${p.name}`}
                                        person={p}
                                        showInviteMode
                                      />
                                    ))
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td style={{ fontSize: '0.8rem' }}>
                                  {partners.length
                                    ? partners.map((p) => (
                                        <div key={`pkg-${p.invoice_id || p.email}`} style={{ marginBottom: '0.35rem' }}>
                                          {p.package}
                                          {p.status !== 'paid' ? ` (${p.status})` : ''}
                                        </div>
                                      ))
                                    : 'Not selected'}
                                </td>
                                <td>{fmtChf(g.paid_chf)}</td>
                                <td>{fmtChf(g.expected_chf)}</td>
                              </WarnRow>
                            );
                          })}
                        </tbody>
                      </Table>
                    </TableWrap>
                    <Pager>
                      <span>
                        Showing {safePackagePage * PACKAGE_PAGE_SIZE + 1}–
                        {Math.min((safePackagePage + 1) * PACKAGE_PAGE_SIZE, filteredPackageTeams.length)} of{' '}
                        {filteredPackageTeams.length} teams (newest first)
                      </span>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                          type="button"
                          disabled={safePackagePage <= 0}
                          onClick={() => setPackagePage((p) => Math.max(0, p - 1))}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          disabled={safePackagePage >= packagePageCount - 1}
                          onClick={() => setPackagePage((p) => Math.min(packagePageCount - 1, p + 1))}
                        >
                          Next
                        </button>
                      </div>
                    </Pager>
                  </>
                )}
              </Section>

              <TwoCol>
                <Section>
                  <h3>Revenue by canton</h3>
                  <p className="sub">{d.revenueByCanton || 'Checkout TSR only. Click → Teams by canton.'}</p>
                  <Table>
                    <thead>
                      <tr>
                        <th>Canton</th>
                        <th>Paid regs</th>
                        <th>Checkout CHF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byCanton.map((r) => (
                        <ClickRow
                          key={r.canton}
                          onClick={() =>
                            go('teams', {
                              canton: r.canton === 'Unknown' ? '' : r.canton,
                            })
                          }
                        >
                          <td>{r.canton}</td>
                          <td>{fmtNum(r.paid)}</td>
                          <td>{fmtChf(r.revenue_checkout_chf)}</td>
                        </ClickRow>
                      ))}
                    </tbody>
                  </Table>
                </Section>
                <Section>
                  <h3>Revenue by category</h3>
                  <p className="sub">{d.revenueBySkill}</p>
                  <Table>
                    <thead>
                      <tr>
                        <th>Skill band</th>
                        <th>Paid teams</th>
                        <th>Checkout CHF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revBySkill.map((r) => (
                        <tr key={r.band}>
                          <td>{r.band}</td>
                          <td>{fmtNum(r.paid_teams)}</td>
                          <td>{fmtChf(r.revenue_checkout_chf)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Section>
              </TwoCol>
            </>
          ) : null}

          <Section>
            <h3>Registration trends (12 months)</h3>
            <p className="sub">
              Each team once — month of their first real signup (excludes phase carry). Sum of months ≈ unique teams.
            </p>
            <BarList rows={trend} labelKey="month" valueKey="registrations" />
            {trend.length > 0 ? (
              <Table style={{ marginTop: '0.75rem' }}>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Registrations</th>
                    <th>Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {trend.map((r) => (
                    <tr key={r.month}>
                      <td>{r.month}</td>
                      <td>{fmtNum(r.registrations)}</td>
                      <td>{fmtNum(r.paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
          </Section>
        </>
      )}
    </Page>
  );
}
